import type { InitialSyncProgress } from '../../lib/sync'

/**
 * Pure copy + progress derivation for the cold-start loading gate, split out from
 * the component so the wording and each state (loading / settled / empty / failed)
 * can be unit-tested without touching animation timing.
 *
 * Voice (design contract v3 "Colours", §3 copy): state a fact and stop. Short,
 * certain, no ellipsis, no apology, no encouragement. This replaced the Journey
 * lens of v2 ("Reaching your OneDrive…", "Setting the horizon…"), whose warmth
 * came from softening every line with a trailing ellipsis and a metaphor.
 *
 * The information is unchanged and the discipline behind it is kept: every
 * string still maps to a *real* lifecycle stage, and no number is spoken until
 * it is genuinely known (the story count only from `merging` onward, the "N of
 * M" only once the queue length is in hand).
 */

/** The visible status line for a phase. `null` progress = the pre-connect beat. */
export function syncStatusLabel(p: InitialSyncProgress | null): string {
  if (!p) return 'Reaching OneDrive'
  switch (p.phase) {
    case 'connecting':
      return 'Reaching OneDrive'
    case 'pulling':
      return 'Retrieving the library'
    case 'merging':
      return p.stories && p.stories > 0
        ? `Bringing ${p.stories} ${plural(p.stories, 'story', 'stories')} together`
        : 'Reconciling'
    case 'saving':
      return p.itemsTotal && p.itemsTotal > 0
        ? `Saving ${Math.min(p.itemsDone ?? 0, p.itemsTotal)} of ${p.itemsTotal}`
        : 'Saving to OneDrive'
    case 'done':
      return p.stories && p.stories > 0
        ? `${p.stories} ${plural(p.stories, 'story', 'stories')} ready`
        : 'The library is ready'
  }
}

/**
 * Fill fraction 0..1 for the horizon bar, or `null` when there is genuinely no
 * per-item signal (connecting / merging / an empty loop) — the gate shows the
 * indeterminate "still walking" ticker in that case rather than a fake number.
 */
export function syncProgressFraction(p: InitialSyncProgress | null): number | null {
  if (!p) return null
  if (p.phase === 'done') return 1
  if ((p.phase === 'pulling' || p.phase === 'saving') && p.itemsTotal && p.itemsTotal > 0) {
    return Math.min(1, (p.itemsDone ?? 0) / p.itemsTotal)
  }
  return null
}

/**
 * Honest copy for a cold-start that failed with no local library to fall back on.
 * States the fact and the reassurance, and does not apologise: "we just couldn't
 * load it right now" was an apology wearing a fact's clothes.
 */
export const FAILED_TITLE = 'Not yet reached OneDrive'
export const FAILED_BODY = 'Your work is safe in OneDrive. This device has not loaded it yet.'

/**
 * Copy for a library that could not be opened (W12 A5). A fact and the reassurance, no apology and
 * no ellipsis (design contract v3 section 3).
 */
export const STORE_FAILED_TITLE = 'This device’s library has not opened'
export const STORE_FAILED_BODY = 'Nothing was changed and nothing was lost. Your work on this device and in OneDrive is exactly as it was.'

function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many
}
